import React, { useState, useEffect, useRef } from 'react';
import styles from './BrewsPage.module.css';
import HeroSection from '../../sections/HeroSection/HeroSection';

const BREWS = [
  // ── Lagers ──
  {
    id: 1,
    name: "Fool's Gold",
    style: "Maize Lager",
    tagline: "Golden. Crisp. Effortlessly refreshing.",
    description: "Brewed with locally sourced maize, Fool's Gold is our take on a modern Indian lager — light-bodied, highly drinkable, and built for easy company. Subtle floral hops meet gentle tropical fruit notes for a beer that stays clean and refreshing right to the last sip.",
    abv: "5.0%", ibu: "12",
    mouthfeel: "Light-bodied, crisp, clean finish",
    aroma: "Mild floral hops, citrus blossom, tropical fruit",
    appearance: "Brilliant pale gold, excellent clarity, bright white head",
    flavour: "Delicate grain sweetness, light floral, tropical fruit, dry finish",
    accent: "#F0CA62",
    src: "https://images.unsplash.com/photo-1608270586620-248524c67de9?w=900&q=80",
  },
  {
    id: 2,
    name: "Safe Bet",
    style: "Indrayani Rice Lager",
    tagline: "Smooth odds. Great reward.",
    description: "Crafted using Maharashtra's prized Indrayani rice, Safe Bet delivers exceptional drinkability with a uniquely clean, almost weightless character. A safe choice that quietly over-delivers.",
    abv: "6.0%", ibu: "14",
    mouthfeel: "Light-bodied, smooth, dry and refreshing",
    aroma: "Fresh grain, delicate malt, subtle floral notes",
    appearance: "Pale straw-gold, crystal clear, lasting white head",
    flavour: "Light grain sweetness, delicate rice character, crisp dry finish",
    accent: "#D4A843",
    src: "https://i.pinimg.com/736x/db/93/97/db939761b81d88ae97815d223f8909bc.jpg",
  },
  {
    id: 3,
    name: "Simply Complicated",
    style: "Jowar Lager",
    tagline: "Simple to drink. Complex to discover.",
    description: "Brewed with indigenous jowar, this lager celebrates India's rich agricultural heritage through a thoughtful modern lens. Approachable at first sip, layered on reflection.",
    abv: "5.5%", ibu: "16",
    mouthfeel: "Medium-light body, crisp finish",
    aroma: "Earthy grain, light fruit esters, floral hops",
    appearance: "Deep golden, bright clarity, persistent white head",
    flavour: "Earthy cereal notes, mild fruitiness, balanced bitterness",
    accent: "#E0B84E",
    src: "https://images.unsplash.com/photo-1518176258769-f227c798150e?w=900&q=80",
  },

  // ── Wheat Beers ──
  {
    id: 4,
    name: "Modern Classic",
    style: "Belgian Witbier",
    tagline: "Timeless Belgian charm with a local twist.",
    description: "Brewed with Lokwan wheat and traditional Belgian-inspired techniques, Modern Classic showcases vibrant citrus character balanced by gentle spice. Old-world soul, new-world grain.",
    abv: "6.0%", ibu: "13",
    mouthfeel: "Soft, silky and refreshing",
    aroma: "Orange peel, coriander, citrus zest, subtle spice",
    appearance: "Hazy pale gold, dense white head",
    flavour: "Bright citrus, gentle spice, soft wheat sweetness, clean finish",
    accent: "#E8C66A",
    src: "https://i.pinimg.com/736x/4f/2f/8a/4f2f8af33c2364e0a40faa1b5d175403.jpg",
  },
  {
    id: 5,
    name: "Clearly Invisible",
    style: "Kristal Weizen",
    tagline: "Crystal clear. Surprisingly expressive.",
    description: "Filtered for brilliance while preserving every nuance of classic weizen character, Clearly Invisible delivers vibrant wheat-driven flavours without the haze. It shouldn't be this good looking and still have this much to say.",
    abv: "5.5%", ibu: "13",
    mouthfeel: "Smooth, medium-light body, creamy finish",
    aroma: "Banana, clove, bubblegum, fresh bread",
    appearance: "Brilliant golden clarity, thick white foam",
    flavour: "Banana esters, mild clove spice, wheat sweetness, subtle bubblegum",
    accent: "#F0CA62",
    src: "https://i.pinimg.com/736x/91/b7/32/91b73222f7a880911452c578e13b0033.jpg",
  },

  // ── Strong & Bold ──
  {
    id: 6,
    name: "Terribly Good",
    style: "Ragi IPA",
    tagline: "Dangerously hoppy. Deliciously balanced.",
    description: "A bold IPA that pairs locally sourced ragi with an aggressive, unapologetic hop profile. The result is something that should be terrible and is, instead, the best decision you'll make all evening.",
    abv: "8.0%", ibu: "103",
    mouthfeel: "Full-bodied, hearty, assertively dry",
    aroma: "Mango, pineapple, passionfruit, citrus, resinous hops",
    appearance: "Deep amber-gold, creamy off-white head",
    flavour: "Intense tropical fruit, grapefruit, pine resin, lingering bitterness",
    accent: "#F4A442",
    src: "https://i.pinimg.com/736x/ee/f2/8d/eef28d3ee32c22052d640865844bde05.jpg",
  },
  {
    id: 7,
    name: "Sleep Walk",
    style: "Imperial Stout",
    tagline: "Dark, decadent and dangerously smooth.",
    description: "A rich, high-strength stout layered with roasted malts and luxurious complexity. Dangerously easy for something this dark. Best consumed when you have nowhere important to be.",
    abv: "8.0%", ibu: "42",
    mouthfeel: "Full-bodied, velvety, warming",
    aroma: "Dark chocolate, espresso, roasted malt, dried fruit",
    appearance: "Opaque black, dense mocha-coloured head",
    flavour: "Chocolate, coffee, molasses, honey, dark fruit, roasted malt",
    accent: "#C19A6B",
    src: "https://i.pinimg.com/736x/f3/77/5d/f3775d5e3c240fd3e67ab6aa526eff45.jpg",
  },

  // ── Ciders ──
  {
    id: 8,
    name: "Anti Gravity",
    style: "Himachali Apple Cider",
    tagline: "Defying expectations with every sip.",
    description: "Made from premium apples sourced from the Himalayan foothills, Anti Gravity is light, sparkling and completely impossible to put down. Physics, apparently, is optional.",
    abv: "4.5%", ibu: null,
    mouthfeel: "Light-bodied, sparkling, crisp",
    aroma: "Fresh apple, orchard fruit, subtle spice",
    appearance: "Pale golden, brilliant clarity",
    flavour: "Juicy apple sweetness, tart freshness, delicate spicy finish",
    accent: "#8DB87A",
    src: "https://images.unsplash.com/photo-1608270586620-248524c67de9?w=900&q=80",
  },
  {
    id: 9,
    name: "Berry Curious",
    style: "Mahabaleshwar Strawberry Cider",
    tagline: "A berry good escape from the ordinary.",
    description: "Crafted with sun-ripened strawberries from the hills of Mahabaleshwar, Berry Curious is exactly what it sounds like — a cider that makes you wonder why you ever settled for anything else. Light, sparkling, and just a little bit irresistible.",
    abv: "4.5%", ibu: null,
    mouthfeel: "Light-bodied, sparkling, crisp",
    aroma: "Fresh strawberries, red berries, orchard fruit",
    appearance: "Pale blush pink, brilliant clarity, lively carbonation",
    flavour: "Fresh strawberry, subtle berry sweetness, gentle tartness, clean finish",
    accent: "#E89BA6",
    src: "https://i.pinimg.com/736x/2f/1f/18/2f1f181c3e29959a006229807eebaa5f.jpg",
  },

  // ── Mead ──
  {
    id: 10,
    name: "Lost & Found",
    style: "Guava Mead",
    tagline: "Where tropical fruit meets ancient craft.",
    description: "A modern interpretation of one of the world's oldest fermented beverages, combining premium wildflower honey with vibrant pink guava. Somewhere between a discovery and a homecoming.",
    abv: "7.0%", ibu: null,
    mouthfeel: "Medium-light body, smooth, silky",
    aroma: "Pink guava, wildflower honey, tropical fruit",
    appearance: "Light rose-gold, brilliant clarity",
    flavour: "Juicy guava, floral honey sweetness, balanced semi-dry finish",
    accent: "#E0A87E",
    src: "https://i.pinimg.com/736x/b2/6b/6b/b26b6b89fcd2364bc4e0691e19a0644b.jpg",
  },
  {
    id: 11,
    name: "Hot Honey",
    style: "Guava Chilli Mead",
    tagline: "Sweet heat. Ancient soul.",
    description: "Ancient craft meets bold flavour in this wildflower honey mead infused with vibrant pink guava and a slow, smouldering chilli warmth. Served with a spiced salt rim, every sip layers sweet, tart and heat in a way that keeps you genuinely curious for the next one.",
    abv: "4.5%", ibu: null,
    mouthfeel: "Light-medium body, smooth, warming finish",
    aroma: "Pink guava, warm chilli, wildflower honey, tropical fruit",
    appearance: "Pale rose-gold, brilliant clarity",
    flavour: "Juicy guava sweetness, floral honey, building chilli heat, lingering spiced finish",
    accent: "#E07B4F",
    src: "https://images.unsplash.com/photo-1535958636474-b021ee887b13?w=900&q=80",
  },
];

// ── Single brew section ───────────────────────────────────────────────────────
const BrewSection = ({ brew, index }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const isEven = index % 2 === 0;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id={`brew-${brew.id}`}
      className={`${styles.brewSection} ${visible ? styles.visible : ''}`}
    >
      {/* Full-bleed image half */}
      <div className={`${styles.imageHalf} ${isEven ? styles.imageLeft : styles.imageRight}`}>
        <img src={brew.src} alt={brew.name} className={styles.bgImg} />
        {/* Dark scrim on the content-facing side */}
        <div className={`${styles.scrim} ${isEven ? styles.scrimRight : styles.scrimLeft}`} />
        {/* Floating brew number */}
        <div className={styles.brewNumber}>
          <span>{String(brew.id).padStart(2, '0')}</span>
          <span className={styles.brewTotal}>/{BREWS.length}</span>
        </div>
      </div>

      {/* Content half */}
      <div className={`${styles.contentHalf} ${isEven ? styles.contentRight : styles.contentLeft}`}>
        <div className={styles.contentInner}>

          {/* Style pill */}
          <span className={styles.stylePill} style={{ borderColor: brew.accent, color: brew.accent }}>
            {brew.style}
          </span>

          {/* Name */}
          <h2 className={styles.brewName}>{brew.name}</h2>

          {/* Tagline */}
          <p className={styles.tagline}>{brew.tagline}</p>

          {/* Description */}
          <p className={styles.description}>{brew.description}</p>

          {/* Gold rule */}
          <div className={styles.rule} style={{ background: brew.accent }} />

          {/* Stats row — ABV always, IBU only when present */}
          <div className={styles.statsRow}>
            <div className={styles.statBig}>
              <span className={styles.statLabel}>ABV</span>
              <span className={styles.statValue} style={{ color: brew.accent }}>{brew.abv}</span>
            </div>
            {brew.ibu && (
              <>
                <div className={styles.statDivider} />
                <div className={styles.statBig}>
                  <span className={styles.statLabel}>IBU</span>
                  <span className={styles.statValue} style={{ color: brew.accent }}>{brew.ibu}</span>
                </div>
              </>
            )}
          </div>

          {/* Attribute grid */}
          <div className={styles.attrGrid}>
            {[
              { label: 'Mouthfeel',  value: brew.mouthfeel  },
              { label: 'Aroma',      value: brew.aroma      },
              { label: 'Appearance', value: brew.appearance },
              { label: 'Flavour',    value: brew.flavour    },
            ].map(({ label, value }) => (
              <div key={label} className={styles.attr}>
                <p className={styles.attrLabel} style={{ color: brew.accent }}>{label}</p>
                <p className={styles.attrValue}>{value}</p>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

// ── Sticky progress nav ───────────────────────────────────────────────────────
// ── Sticky progress nav ───────────────────────────────────────────────────────
const BrewNav = ({ active, total }) => (
  <nav className={styles.brewNav} aria-label="Brew navigation">
    <div className={styles.navTrack}>
      <div
        className={styles.navFill}
        style={{ height: `${((active - 1) / (total - 1)) * 100}%` }}
      />
    </div>
    {BREWS.map(b => (
      <a key={b.id}
        href={`#brew-${b.id}`}
        className={`${styles.navDot} ${active === b.id ? styles.navDotActive : ''}`}
        title={b.name}
        aria-label={b.name}
        style={active === b.id ? { background: b.accent, borderColor: b.accent } : {}}
      />
    ))}
  </nav>
);
// ── Page ──────────────────────────────────────────────────────────────────────
const BrewsPage = () => {
  const [active, setActive] = useState(1);

  useEffect(() => {
    const observers = BREWS.map(brew => {
      const el = document.getElementById(`brew-${brew.id}`);
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([e]) => { if (e.isIntersecting) setActive(brew.id); },
        { threshold: 0.35 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach(o => o?.disconnect());
  }, []);

  return (
    <div className={styles.page}>
      <HeroSection
        title="Meet The Brews"
        image="https://i.pinimg.com/736x/df/01/6c/df016c969c54427aebd3880d8bebd2ae.jpg"
        imageAlt="Berli Brewing taproom"
      />

      {BREWS.map((brew, i) => (
        <BrewSection key={brew.id} brew={brew} index={i} />
      ))}

      <BrewNav active={active} total={BREWS.length} />
    </div>
  );
};

export default BrewsPage;